import { useState } from "react";

import { cn } from "~/lib/utils";
import { useOrgProfile } from "../../state/orgProfile";
import { Switch } from "../ui/switch";
import { useRegulationOrg } from "./useRegulationOrg";

/**
 * Regulatory monitoring for the active organization — the switch that fills
 * this page. It writes the same `Organization.regulatoryMonitoringEnabled`
 * column the domain API owns, and only admins may flip it.
 */
export function RegulatoryMonitoringToggle({
  compact = false,
  className,
}: {
  readonly compact?: boolean;
  /** The compact switch's own layout, e.g. a full-width menu row. */
  readonly className?: string;
}) {
  // Resolve the tenant explicitly: falling back to the first org would flip
  // monitoring for an organization the user is not looking at.
  const { org } = useRegulationOrg();
  const { profile, canManage, save } = useOrgProfile(org?.id ?? null);
  const [error, setError] = useState<string | null>(null);
  const [isSaving, setIsSaving] = useState(false);

  if (!profile) {
    return null;
  }

  const onChange = (checked: boolean) => {
    setIsSaving(true);
    setError(null);
    void save({ regulatoryMonitoringEnabled: checked }).then((failure) => {
      setIsSaving(false);
      setError(failure);
    });
  };

  if (compact) {
    // In a header or a menu: one word and the switch. The explanation lives
    // on the page it fills; a header is not the place to restate it.
    return (
      <label className={cn("flex items-center gap-2 text-xs text-muted-foreground", className)}>
        {error ? <span className="text-destructive">{error}</span> : "Monitoring"}
        <Switch
          checked={profile.regulatoryMonitoringEnabled}
          disabled={!canManage || isSaving}
          aria-label="Regulatory monitoring"
          onCheckedChange={(checked) => onChange(Boolean(checked))}
        />
      </label>
    );
  }

  return (
    <div className="mt-6 flex items-center justify-between gap-4 rounded-xl border border-border/60 px-4 py-3">
      <div className="min-w-0">
        <p className="text-sm font-medium text-foreground">Regulatory monitoring</p>
        <p className="mt-0.5 max-w-xl text-[13px] leading-[1.45] text-muted-foreground/80">
          {canManage
            ? "Track regulator publications for this organization's jurisdictions and surface what changed."
            : "Only organization admins can turn monitoring on or off."}
        </p>
        {error ? (
          <p role="alert" className="mt-1 text-xs text-destructive">
            {error}
          </p>
        ) : null}
      </div>
      <Switch
        checked={profile.regulatoryMonitoringEnabled}
        disabled={!canManage || isSaving}
        aria-label="Regulatory monitoring"
        onCheckedChange={(checked) => {
          setIsSaving(true);
          setError(null);
          void save({ regulatoryMonitoringEnabled: Boolean(checked) }).then((failure) => {
            setIsSaving(false);
            setError(failure);
          });
        }}
      />
    </div>
  );
}
